import classNames from "classnames";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  AddOnFeeCategory,
  EContractType,
  EEscalationStatus,
  ESenderRole,
  IContractDetailsResponseData,
  IContractEscalationDetails,
  IEscalationProposedSettlement,
  IPartyCashOut,
} from "../../../../setup/api/services/contract/models";
import { EContractActions } from "../../../../setup/api/services/contractActions/enum";
import { IContractActionRequestBody } from "../../../../setup/api/services/contractActions/models";
import { doAction } from "../../../../setup/api/services/contractActions";
import { AuthConfig } from "../../../../setup/context/config/authConfig";
import { useOrganizationCode } from "../../../../setup/util/extractOrganizationCode";
import SettlementModal from "./SettlementModal";
import RejectSettlementModal from "./RejectSettlementModal";
import AcceptSettlementModal from "./AcceptSettlementModal";
import SupportingDocumentsModal from "./SupportingDocumentsModal";
import bannerStyles from "./styles.module.scss";

interface EscalationSectionProps {
  milestone: IContractDetailsResponseData | undefined;
}

const formatDate = (iso: string) =>
  new Date(iso).toLocaleString("ar-EG", {
    day: "numeric",
    month: "long",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });

const getEscalationProposalParties = (
  proposed: IEscalationProposedSettlement | null | undefined
): IPartyCashOut[] => {
  if (!proposed) return [];
  return Array.isArray(proposed) ? proposed : proposed.parties ?? [];
};

const WAFFY_PROPOSER_LOGO_SRC = "/assets/images/svg/waffyBlue.svg";

const EscalationSection = ({ milestone }: EscalationSectionProps) => {
  const { t } = useTranslation();
  const tokenOrgCode = useOrganizationCode(AuthConfig.getToken()) || undefined;
  const [proposalOpen, setProposalOpen] = useState(false);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [acceptOpen, setAcceptOpen] = useState(false);
  const [rejectionComment, setRejectionComment] = useState("");
  const [acceptLoading, setAcceptLoading] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [docsTicketId, setDocsTicketId] = useState<string | null>(null);

  const allowResolveDispute = milestone?.organization?.allowResolveDispute;

  const allProfiles = useMemo(() => {
    return [
      ...(milestone?.partiesProfiles?.CUSTOMER || []).map((p) => ({
        ...p,
        roleKey: "CUSTOMER" as const,
      })),
      ...(milestone?.partiesProfiles?.PROVIDER || []).map((p) => ({
        ...p,
        roleKey: "PROVIDER" as const,
      })),
      ...(milestone?.partiesProfiles?.BROKER || []).map((p) => ({
        ...p,
        roleKey: "BROKER" as const,
      })),
    ];
  }, [milestone?.partiesProfiles]);

  const resolvePartyName = useCallback(
    (userId: string, userRole: ESenderRole) => {
      const match =
        allProfiles.find(
          (p) =>
            String(p.id) === userId ||
            String(p.userIdentification) === userId ||
            p.userId === userId
        ) || null;
      if (match?.profile?.fullName) return match.profile.fullName;
      const isCustomer = userRole === ESenderRole.CUSTOMER;
      if (isCustomer) return milestone?.customer?.profile?.fullName || "";
      return milestone?.provider?.profile?.fullName || "";
    },
    [allProfiles, milestone?.customer, milestone?.provider]
  );

  const resolveSettlementParties = useCallback(
    (parties: IPartyCashOut[]) => {
      const getInitials = (fullName: string) => {
        const parts = fullName.trim().split(/\s+/);
        return parts.length >= 2
          ? `${parts[0][0]}${parts[1][0]}`
          : fullName.slice(0, 2);
      };

      return parties.map((item) => {
        const itemId = String(item.id);
        const itemUid = item.userIdentification
          ? String(item.userIdentification)
          : itemId;

        const match = allProfiles.find(
          (p) =>
            String(p.userIdentification) === itemUid || String(p.id) === itemId
        );
        if (match) {
          const roleLabel =
            match.roleKey === "CUSTOMER"
              ? t("lbl_buyer")
              : match.roleKey === "PROVIDER"
              ? t("lbl_seller")
              : t("lbl_broker");
          const fullName = match.profile?.fullName || itemUid;
          return {
            name: fullName,
            roleLabel,
            amount: item.amountDue,
            phoneNumber: match.phoneNumber || "",
            photoUrl: match.profile?.photoUrl || "",
            initials: getInitials(fullName),
          };
        }
        if (String(milestone?.customer?.userIdentification) === itemUid) {
          const fullName = milestone?.customer?.profile?.fullName || itemUid;
          return {
            name: fullName,
            roleLabel: t("lbl_buyer"),
            amount: item.amountDue,
            phoneNumber: milestone?.customer?.phoneNumber || "",
            photoUrl: milestone?.customer?.profile?.photoUrl || "",
            initials: getInitials(fullName),
          };
        }
        if (String(milestone?.provider?.userIdentification) === itemUid) {
          const fullName = milestone?.provider?.profile?.fullName || itemUid;
          return {
            name: fullName,
            roleLabel: t("lbl_seller"),
            amount: item.amountDue,
            phoneNumber: milestone?.provider?.phoneNumber || "",
            photoUrl: milestone?.provider?.profile?.photoUrl || "",
            initials: getInitials(fullName),
          };
        }
        return {
          name: itemUid,
          roleLabel: "",
          amount: item.amountDue,
          phoneNumber: "",
          photoUrl: "",
          initials: itemUid.slice(0, 2),
        };
      });
    },
    [allProfiles, milestone?.customer, milestone?.provider, t]
  );

  const renderRejectionBlock = useCallback(
    (rejectionComment: string) => (
      <>
        <div className={bannerStyles.divider} />
        <div className={bannerStyles.timelineItem}>
          <div className={bannerStyles.cardHeader}>
            <div className={bannerStyles.headerMain}>
              <div className={bannerStyles.escalationLeadingSlot}>
                <span className={bannerStyles.rejectedIcon}>✕</span>
              </div>
              <div className={bannerStyles.headerInfo}>
                <span className={bannerStyles.escalatedByLabel}>
                  {t("lbl_settlement_rejected_reason")}
                </span>
              </div>
            </div>
          </div>
          <p className={bannerStyles.message}>{rejectionComment}</p>
        </div>
      </>
    ),
    [t]
  );

  const sortedEscalations = useMemo(() => {
    return [...(milestone?.escalations || [])].sort(
      (a, b) =>
        new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
    );
  }, [milestone?.escalations]);

  const activeEscalation = useMemo(
    () => milestone?.escalations?.find((e) => !e.resolved),
    [milestone?.escalations]
  );

  const activeEscalationIsProposed = useMemo(
    () =>
      !!activeEscalation &&
      (activeEscalation.status === EEscalationStatus.RESOLVED_CUSTOMER ||
        activeEscalation.status === EEscalationStatus.RESOLVED_PROVIDER),
    [activeEscalation]
  );

  const handleAccept = useCallback(async () => {
    const contractId = milestone?.id;
    const actorOrgCode =
      milestone?.organizationCode ||
      milestone?.organization?.code ||
      tokenOrgCode;
    if (!contractId || !actorOrgCode) return;

    setAcceptLoading(true);
    try {
      const body: IContractActionRequestBody = {
        contractId,
        userId: String(AuthConfig.getUserInfo().userIdentification),
        actorRole: ESenderRole.CLIENT_ADMIN,
        actorOrgCode,
        contractAction: EContractActions.CLIENT_ACCEPT_SETTLEMENT,
      };
      await doAction(body);
      setAcceptOpen(false);
      window.location.reload();
    } finally {
      setAcceptLoading(false);
    }
  }, [milestone, tokenOrgCode]);

  const handleReject = useCallback(async () => {
    if (!rejectionComment.trim()) return;
    const contractId = milestone?.id;
    const actorOrgCode =
      milestone?.organizationCode ??
      milestone?.organization?.code ??
      tokenOrgCode;

    if (!contractId || !actorOrgCode) return;

    setSubmitLoading(true);
    try {
      const body: IContractActionRequestBody = {
        contractId,
        userId: String(AuthConfig.getUserInfo().userIdentification),
        actorRole: ESenderRole.CLIENT_ADMIN,
        actorOrgCode,
        contractAction: EContractActions.CLIENT_REJECT_SETTLEMENT,
        escalationDTO: { rejectionComment },
      };
      await doAction(body);
      setRejectOpen(false);
      window.location.reload();
    } finally {
      setSubmitLoading(false);
    }
  }, [milestone, rejectionComment, tokenOrgCode]);

  if (!milestone || milestone.type !== EContractType.MILESTONE_CONTRACT)
    return null;
  if (!sortedEscalations.length) return null;

  const getStatusLabel = (esc: IContractEscalationDetails) => {
    switch (esc.status) {
      case EEscalationStatus.OPEN:
        return allowResolveDispute
          ? t("lbl_escalation_open_can_propose")
          : t("lbl_waiting_waffy_settlement");
      case EEscalationStatus.RESOLVED_CUSTOMER:
      case EEscalationStatus.RESOLVED_PROVIDER:
        return t("lbl_waiting_third_party_approval");
      case EEscalationStatus.PENDING_SETTLEMENT:
        return t("lbl_escalation_cashout_pending");
      case EEscalationStatus.SETTLED:
        return t("lbl_escalation_settled_status");
      default:
        return t("lbl_escalation_under_review");
    }
  };

  return (
    <>
      <div className={bannerStyles.wrapper}>
        <div className={bannerStyles.sectionHeaderRow}>
          <h3 className={bannerStyles.sectionTitle}>{t("lbl_escalation")}</h3>
          {activeEscalationIsProposed && (
            <div className={bannerStyles.headerActionButtons}>
              <button
                type="button"
                className={bannerStyles.rejectBtn}
                onClick={() => setRejectOpen(true)}>
                {t("lbl_reject")}
              </button>
              <button
                type="button"
                className={bannerStyles.acceptBtn}
                onClick={() => setAcceptOpen(true)}>
                {t("lbl_accept_settlement")}
              </button>
            </div>
          )}
        </div>

        <div className={bannerStyles.card}>
          {sortedEscalations.map((esc, index) => {
            const isActive = !esc.resolved;
            const isNotFirst = index > 0;
            const isEscCustomer = esc.userRole === ESenderRole.CUSTOMER;
            const partyName = resolvePartyName(esc.userId, esc.userRole);
            const roleLabel = isEscCustomer ? t("lbl_buyer") : t("lbl_seller");
            const partyLabel = partyName
              ? `${roleLabel} (${partyName})`
              : roleLabel;

            const isOpen = esc.status === EEscalationStatus.OPEN;
            const isProposed =
              esc.status === EEscalationStatus.RESOLVED_CUSTOMER ||
              esc.status === EEscalationStatus.RESOLVED_PROVIDER;
            const isPendingOrSettled =
              esc.status === EEscalationStatus.PENDING_SETTLEMENT ||
              esc.status === EEscalationStatus.SETTLED;

            const proposalParties = getEscalationProposalParties(
              esc.proposedSettlement
            );
            const settlementParties = proposalParties.length
              ? resolveSettlementParties(proposalParties)
              : [];

            const showSettlement =
              settlementParties.length > 0 &&
              (isProposed ||
                isPendingOrSettled ||
                esc.resolved ||
                !!esc.rejectionComment);

            const showRejection = !!esc.rejectionComment;
            const showProposeButton =
              isActive && isOpen && !!allowResolveDispute;

            return (
              <div
                key={esc.ticketId || index}
                className={bannerStyles.escalationGroup}>
                {isNotFirst && <div className={bannerStyles.groupDivider} />}

                <div className={bannerStyles.timelineItem}>
                  <div className={bannerStyles.cardHeader}>
                    <div className={bannerStyles.headerMain}>
                      <div className={bannerStyles.escalationLeadingSlot}>
                        <img
                          src="/assets/images/svg/escalationWarningIcon.svg"
                          alt=""
                          className={bannerStyles.warningIcon}
                          onError={(e) => {
                            (e.target as HTMLImageElement).style.display =
                              "none";
                          }}
                        />
                      </div>
                      <div className={bannerStyles.headerInfo}>
                        <span className={bannerStyles.escalatedByLabel}>
                          {t("lbl_escalated_by")}
                        </span>
                        <span className={bannerStyles.partyName}>
                          {partyLabel}
                        </span>
                        {esc.reason && (
                          <span className={bannerStyles.reasonLabel}>
                            {esc.reason}
                          </span>
                        )}
                        {esc.message &&
                          !esc.reason?.trim().includes(esc.message?.trim()) && (
                            <p className={bannerStyles.message}>
                              {esc.message}
                            </p>
                          )}
                      </div>
                    </div>
                    {esc.createdAt && (
                      <span className={bannerStyles.date}>
                        {formatDate(esc.createdAt)}
                      </span>
                    )}
                  </div>
                  {esc.supportingDocuments && esc.supportingDocuments.length > 0 && (
                    <button
                      type="button"
                      className={bannerStyles.attachmentsBtn}
                      onClick={() => setDocsTicketId(esc.ticketId)}>
                      📎 {t("lbl_attachments")} ({esc.supportingDocuments.length})
                    </button>
                  )}
                </div>

                {showRejection &&
                  !isOpen &&
                  renderRejectionBlock(esc.rejectionComment as string)}

                {showSettlement &&
                  (() => {
                    const waffyFee = proposalParties.reduce(
                      (sum, item) =>
                        sum + (item.amountDue || 0) * (item.feePercentage || 0),
                      0
                    );
                    const shippingFee =
                      milestone?.addonFeesList?.find(
                        (f) => f.type === AddOnFeeCategory.SHIPPING_FEES
                      )?.amount || 0;
                    const serviceFee =
                      milestone?.addonFeesList?.find(
                        (f) => f.type === AddOnFeeCategory.SERVICE_FEES
                      )?.amount || 0;
                    const orgName = milestone?.organization?.name;

                    const feesBreakdown = [
                      ...(serviceFee > 0
                        ? [
                            {
                              label: orgName
                                ? `${t("lbl_third_party")} (${orgName})`
                                : t("lbl_third_party"),
                              amount: serviceFee,
                            },
                          ]
                        : []),
                      ...(shippingFee > 0
                        ? [
                            {
                              label: t("lbl_shipping_fees"),
                              amount: shippingFee,
                            },
                          ]
                        : []),
                      ...(waffyFee > 0
                        ? [{ label: t("lbl_waffy_fees"), amount: waffyFee }]
                        : []),
                    ];

                    const splitPhone = (phone: string) => {
                      const trimmed = phone.trim();
                      const match = trimmed.match(/^(\+\d{1,4})\s?(.*)$/);
                      return match
                        ? { code: match[1], rest: match[2] }
                        : { code: "", rest: trimmed };
                    };

                    const buyerLabel = t("lbl_buyer");

                    // updatedBy === senderId means the org's client admin proposed
                    const updatedBy = esc.proposedSettlementUpdatedBy;
                    const isClientAdminProposer =
                      !!updatedBy && updatedBy === String(milestone?.senderId);
                    const proposalDate =
                      esc.proposedSettlementUpdatedAt ?? undefined;
                    const proposerName = isClientAdminProposer
                      ? milestone?.organization?.name || t("lbl_client_admin")
                      : t("lbl_waffy");
                    const proposalLogoSrc = isClientAdminProposer
                      ? milestone?.organization?.logoUrl ||
                        WAFFY_PROPOSER_LOGO_SRC
                      : WAFFY_PROPOSER_LOGO_SRC;

                    return (
                      <>
                        <div className={bannerStyles.divider} />
                        <div className={bannerStyles.proposalSection}>
                          <div className={bannerStyles.proposalHeader}>
                            <div className={bannerStyles.proposalHeaderInfo}>
                              <div
                                className={bannerStyles.escalationLeadingSlot}>
                                <img
                                  src={proposalLogoSrc}
                                  alt={proposerName}
                                  className={bannerStyles.proposalProposerLogo}
                                  onError={(e) => {
                                    const el = e.target as HTMLImageElement;
                                    if (el.src.includes("waffyLogo.svg")) {
                                      el.style.display = "none";
                                      return;
                                    }
                                    el.src = WAFFY_PROPOSER_LOGO_SRC;
                                  }}
                                />
                              </div>
                              <div className={bannerStyles.proposalHeaderTexts}>
                                <span className={bannerStyles.proposalLabel}>
                                  {t("lbl_proposed_settlement")}
                                </span>
                                <span className={bannerStyles.proposalActor}>
                                  {proposerName}
                                </span>
                              </div>
                            </div>
                            {proposalDate && (
                              <span className={bannerStyles.proposalDate}>
                                {formatDate(proposalDate)}
                              </span>
                            )}
                          </div>
                          <div className={bannerStyles.partyList}>
                            {settlementParties.map((party, i) => {
                              const { code, rest } = splitPhone(
                                party.phoneNumber
                              );
                              const badgeClass =
                                party.roleLabel === buyerLabel
                                  ? bannerStyles.roleBadgeBuyer
                                  : party.roleLabel === t("lbl_broker")
                                  ? bannerStyles.roleBadgeBroker
                                  : bannerStyles.roleBadgeSeller;
                              return (
                                <div key={i} className={bannerStyles.partyRow}>
                                  <div className={bannerStyles.partyMeta}>
                                    <div
                                      className={classNames(
                                        bannerStyles.escalationLeadingSlot,
                                        bannerStyles.escalationLeadingSlotPartyAvatar
                                      )}>
                                      <div className={bannerStyles.avatar}>
                                        {party.photoUrl ? (
                                          <img
                                            src={party.photoUrl}
                                            alt=""
                                            className={bannerStyles.avatarImg}
                                            onError={(e) => {
                                              (
                                                e.target as HTMLImageElement
                                              ).style.display = "none";
                                            }}
                                          />
                                        ) : (
                                          <span
                                            className={
                                              bannerStyles.avatarInitials
                                            }>
                                            {party.initials}
                                          </span>
                                        )}
                                      </div>
                                    </div>
                                    <div className={bannerStyles.partyInfo}>
                                      <div className={bannerStyles.nameAndTag}>
                                        <span
                                          className={
                                            bannerStyles.partyFullName
                                          }>
                                          {party.name}
                                        </span>
                                        {party.roleLabel && (
                                          <span
                                            className={classNames(
                                              bannerStyles.roleBadge,
                                              badgeClass
                                            )}>
                                            {party.roleLabel}
                                          </span>
                                        )}
                                      </div>
                                      {party.phoneNumber && (
                                        <div
                                          className={bannerStyles.mobileNumber}>
                                          {code && (
                                            <span
                                              className={
                                                bannerStyles.countryCode
                                              }>
                                              {code}
                                            </span>
                                          )}
                                          <span
                                            className={
                                              bannerStyles.phoneNumber
                                            }>
                                            {rest}
                                          </span>
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                  <div className={bannerStyles.partyAmount}>
                                    <span className={bannerStyles.amountLabel}>
                                      {t("lbl_amount_due")}
                                    </span>
                                    <span className={bannerStyles.amountValue}>
                                      {party.amount.toFixed(0)}
                                      {t("lbl_saudi_currency")}
                                    </span>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                          {feesBreakdown.length > 0 && (
                            <div className={bannerStyles.feesRow}>
                              {feesBreakdown.map((fee, i) => (
                                <div key={i} className={bannerStyles.feeItem}>
                                  <span className={bannerStyles.feeLabel}>
                                    {fee.label}
                                  </span>
                                  <span className={bannerStyles.feeValue}>
                                    {fee.amount.toFixed(0)}
                                    {t("lbl_saudi_currency")}
                                  </span>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      </>
                    );
                  })()}

                {showRejection &&
                  isOpen &&
                  renderRejectionBlock(esc.rejectionComment as string)}

                {isPendingOrSettled && (
                  <>
                    <div className={bannerStyles.divider} />
                    <div className={bannerStyles.timelineItem}>
                      <div className={bannerStyles.cardHeader}>
                        <div className={bannerStyles.headerMain}>
                          <div className={bannerStyles.escalationLeadingSlot}>
                            <img
                              src="/assets/images/svg/green_tick.svg"
                              alt=""
                              className={bannerStyles.warningIcon}
                              onError={(e) => {
                                (e.target as HTMLImageElement).style.display =
                                  "none";
                              }}
                            />
                          </div>
                          <div className={bannerStyles.headerInfo}>
                            <span className={bannerStyles.escalatedByLabel}>
                              {t("lbl_escalation_approved_settlement")}
                            </span>
                            <span className={bannerStyles.partyName}>
                              {milestone?.organization?.name ||
                                t("lbl_client_admin")}
                            </span>
                          </div>
                        </div>
                        {esc.resolvedAt && (
                          <span className={bannerStyles.date}>
                            {formatDate(esc.resolvedAt)}
                          </span>
                        )}
                      </div>
                    </div>
                  </>
                )}

                <div className={bannerStyles.divider} />

                {isActive && isProposed ? (
                  <div className={bannerStyles.statusPill}>
                    <img
                      src="/assets/images/svg/escalationStatusClock.svg"
                      alt=""
                      className={bannerStyles.clockIcon}
                      onError={(e) => {
                        (e.target as HTMLImageElement).style.display = "none";
                      }}
                    />
                    <span>{t("lbl_waiting_for_approval_title")}</span>
                  </div>
                ) : showProposeButton ? (
                  <button
                    type="button"
                    className={bannerStyles.resolveButton}
                    onClick={() => setProposalOpen(true)}>
                    {t("lbl_resolve_dispute")}
                  </button>
                ) : (
                  <div className={bannerStyles.statusPill}>
                    <img
                      src="/assets/images/svg/escalationStatusClock.svg"
                      alt=""
                      className={bannerStyles.clockIcon}
                      onError={(e) => {
                        (e.target as HTMLImageElement).style.display = "none";
                      }}
                    />
                    <span>{getStatusLabel(esc)}</span>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      <AcceptSettlementModal
        open={acceptOpen}
        onClose={() => setAcceptOpen(false)}
        onConfirm={handleAccept}
        loading={acceptLoading}
      />

      <RejectSettlementModal
        open={rejectOpen}
        onClose={() => {
          setRejectOpen(false);
          setRejectionComment("");
        }}
        onConfirm={handleReject}
        loading={submitLoading}
        rejectionComment={rejectionComment}
        setRejectionComment={setRejectionComment}
      />

      {activeEscalation && allowResolveDispute && (
        <SettlementModal
          open={proposalOpen}
          handleClose={() => setProposalOpen(false)}
          contract={milestone}
          propose={true}
        />
      )}

      {docsTicketId && (() => {
        const docs = sortedEscalations.find(e => e.ticketId === docsTicketId)?.supportingDocuments;
        return docs?.length ? (
          <SupportingDocumentsModal
            open={true}
            onClose={() => setDocsTicketId(null)}
            documents={docs}
          />
        ) : null;
      })()}
    </>
  );
};

export default EscalationSection;
